import React, { Fragment, useState, useEffect } from 'react';
import { FaPhone, FaKey, FaUser } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { IoMdArrowRoundBack } from "react-icons/io";

function UserLoginPage() {
    const [mobile, setMobile] = useState('');
    const [otp, setOtp] = useState('');
    const [generatedOtp, setGeneratedOtp] = useState(null);
    const [otpSent, setOtpSent] = useState(false);
    const [username, setUsername] = useState('');
    const [usernameRequired, setUsernameRequired] = useState(false);
    const [sendingOtp, setSendingOtp] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [resendTimer, setResendTimer] = useState(0); // seconds remaining to allow resend
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;

    // Countdown effect for resend
    useEffect(() => {
        if (resendTimer <= 0) return;
        const t = setTimeout(() => setResendTimer(prev => prev - 1), 1000);
        return () => clearTimeout(t);
    }, [resendTimer]);

    const handleSendOtp = async (isResend = false) => {
        if (sendingOtp) return;
        const trimmed = mobile.trim();
        if (!/^\d{10}$/.test(trimmed)) {
            toast.error('Enter valid 10-digit mobile number');
            return;
        }
        try {
            setSendingOtp(true);
            const response = await axios.post(`${API_URL}/verify-user`, { mobileNumber: trimmed });
            setGeneratedOtp(response.data.otp);
            setOtpSent(true);
            setUsernameRequired(response.data.usernameMissing);
            setResendTimer(30); // 30s cooldown
            toast.success(isResend ? 'OTP resent' : 'OTP sent');
        } catch (error) {
            toast.error('Failed to send OTP');
            console.error(error);
        } finally {
            setSendingOtp(false);
        }
    };

    const handleLogin = async () => {
        if (verifying) return;
        if (!otp.trim()) { toast.error('Enter OTP'); return; }
        if (otp !== generatedOtp?.toString()) { toast.error('Incorrect OTP'); return; }
        if (usernameRequired) {
            const uname = username.trim();
            if (uname.length < 3) { toast.error('Username min 3 chars'); return; }
        }
        try {
            setVerifying(true);
            if (usernameRequired) {
                await axios.post(`${API_URL}/update-username`, { mobile: mobile.trim(), username: username.trim() });
            }
            localStorage.setItem('mobile', mobile.trim());
            toast.success('Login successful');
            setTimeout(() => navigate('/'), 1000);
        } catch (error) {
            toast.error('Login failed');
            console.error(error);
        } finally {
            setVerifying(false);
        }
    };

    const canResend = otpSent && resendTimer === 0 && !sendingOtp;
    const showResend = otpSent; // whether to show resend UI once OTP sent

    return (
        <Fragment>
            <div className='container-fluid m-t25'>
                <div className='row'>
                    <div className='col-12 cat-strip border shadow-sm'>
                        <div>
                            <button onClick={() => window.history.back()} aria-label='Go Back'>
                                <IoMdArrowRoundBack />
                            </button>
                            <label>USER LOGIN</label>
                        </div>
                    </div>
                </div>
                <div className='row justify-content-center'>
                    <div className='col-xl-4 col-lg-5 col-md-6 col-sm-9 col-11 mt-4'>
                        <div className='login-modal-content p-4' style={{borderRadius:'6px'}}> 
                            {/* <h6 className='fw-bold text-center mb-3' style={{letterSpacing:'0.5px'}}>LOGIN / VERIFY MOBILE</h6> */}
                            {/* Mobile Field */}
                            <div className={`bb-field ${mobile && !/^\d{10}$/.test(mobile)?'error':''}`}> 
                                <FaPhone className='bb-ico' />
                                <input
                                    type='tel'
                                    inputMode='numeric'
                                    maxLength={10}
                                    placeholder='Mobile Number *'
                                    value={mobile}
                                    onChange={(e)=> setMobile(e.target.value.replace(/[^0-9]/g,'').slice(0,10))}
                                    aria-label='Mobile Number'
                                />
                                {mobile && mobile.length!==10 && <span className='bb-msg'>10 digits required</span>}
                            </div>
                            {!otpSent && (
                                <button
                                    type='button'
                                    className='w-100 btn btn-danger mb-3'
                                    onClick={()=>handleSendOtp(false)}
                                    disabled={sendingOtp || !/^\d{10}$/.test(mobile)}
                                >
                                    {sendingOtp ? <span className='spinner-border spinner-border-sm'/> : 'Get OTP'}
                                </button>
                            )}
                            {otpSent && (
                                <>
                                    <div className={`bb-field ${otp && !/^\d{6}$/.test(otp)?'error':''}`}> 
                                        <FaKey className='bb-ico' />
                                        <input
                                            type='tel'
                                            inputMode='numeric'
                                            maxLength={6}
                                            placeholder='Enter 6 digit OTP *'
                                            value={otp}
                                            onChange={(e)=> setOtp(e.target.value.replace(/[^0-9]/g,'').slice(0,6))}
                                            aria-label='OTP'
                                        />
                                        {otp && otp.length!==6 && <span className='bb-msg'>6 digits required</span>}
                                    </div>
                                    <button
                                        type='button'
                                        className='w-100 btn btn-danger mb-3'
                                        onClick={()=>handleSendOtp(true)}
                                        disabled={!canResend}
                                    >
                                        {canResend ? 'Resend OTP' : `Resend in ${resendTimer}s`}
                                    </button>
                                    {usernameRequired && (
                                        <div className={`bb-field ${username && username.trim().length<3 ? 'error':''}`}> 
                                            <FaUser className='bb-ico' />
                                            <input
                                                type='text'
                                                placeholder='Choose a username *'
                                                value={username}
                                                onChange={(e)=> setUsername(e.target.value)}
                                                aria-label='Username'
                                                maxLength={30}
                                            />
                                            {username && username.trim().length<3 && <span className='bb-msg'>Min 3 chars</span>}
                                        </div>
                                    )}
                                    <button
                                        type='button'
                                        className='w-100 btn btn-danger'
                                        onClick={handleLogin}
                                        disabled={verifying || !/^\d{6}$/.test(otp) || (usernameRequired && username.trim().length<3)}
                                    >
                                        {verifying ? <span className='spinner-border spinner-border-sm'/> : 'Verify & Continue'}
                                    </button>
                                </>
                            )}
                            <div className='small text-muted text-center mt-3' style={{fontSize:'11px'}}>We send OTP only for verification. It is free & fast.</div>
                        </div>
                    </div>
                </div>
            </div>
            <ToastContainer />
        </Fragment>
    );
}

export default UserLoginPage;
